import LRUCache from '@fiatjaf/lru-cache/lru-cache'
import { NostrUserRequest } from '@nostr/gadgets/metadata'
import { queryProfile } from '@nostr/tools/nip05'
import { decode } from '@nostr/tools/nip19'

export function formatNpub(npub: string, length = 14) {
  if (length < 12) {
    length = 12
  }

  if (length >= 63) {
    return npub
  }

  const prefixLength = Math.floor((length - 5) / 2) + 5
  const suffixLength = length - prefixLength
  return npub.slice(0, prefixLength) + '…' + npub.slice(-suffixLength)
}

export function isValidPubkey(pubkey: string) {
  return /^[0-9a-f]{64}$/.test(pubkey)
}

const pubkeyImageCache = new LRUCache<string, string>(1000)
export function generateImageByPubkey(pubkey: string): string {
  if (pubkeyImageCache.has(pubkey)) {
    return pubkeyImageCache.get(pubkey)!
  }

  const hexByte = (offset: number) => parseInt(pubkey.slice(offset, offset + 2), 16) || 0

  // base hue straight from the first byte, no math beyond a mod
  const baseHue = hexByte(0) * 1.4 // 0-255 -> 0-357ish
  const blobCount = 3

  const blobs = Array.from({ length: blobCount }, (_, i) => {
    const off = i * 6 // walk forward through the hex string, 3 bytes per blob
    const cx = hexByte(off) % 100
    const cy = hexByte(off + 2) % 100
    const r = 30 + (hexByte(off + 4) % 30)
    const hue = (baseHue + i * (360 / blobCount)) % 360
    const sat = 45 + (hexByte(off + 4) % 20)
    const light = 45 + (hexByte(off + 2) % 15)
    return { cx, cy, r, color: `hsl(${hue.toFixed(0)}, ${sat}%, ${light}%)` }
  })

  const bgHue = (baseHue + 180) % 360
  const bgColor = `hsl(${bgHue.toFixed(0)}, 35%, 65%)`

  const defs = blobs
    .map(
      (b, i) => `
        <radialGradient id="g${i}-${pubkey}" cx="${b.cx}%" cy="${b.cy}%" r="${b.r}%">
          <stop offset="0%" stop-color="${b.color}" stop-opacity="0.95" />
          <stop offset="100%" stop-color="${b.color}" stop-opacity="0" />
        </radialGradient>`
    )
    .join('')

  const layers = blobs
    .map((_, i) => `<rect width="100%" height="100%" fill="url(#g${i}-${pubkey})" />`)
    .join('')

  const svg = `
    <svg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <defs>
        ${defs}
        <filter id="blur-${pubkey}"><feGaussianBlur stdDeviation="2" /></filter>
      </defs>
      <rect width="100%" height="100%" fill="${bgColor}" />
      <g filter="url(#blur-${pubkey})">${layers}</g>
    </svg>
  `

  const image = `data:image/svg+xml,${encodeURIComponent(svg)}`

  pubkeyImageCache.set(pubkey, image)

  return image
}

export async function resolveProfileInput(input: string): Promise<NostrUserRequest> {
  if (input.includes('@') && !input.startsWith('nostr:')) {
    const resolved = await queryProfile(input)
    if (resolved) {
      return { pubkey: resolved.pubkey }
    }
  }

  if (isValidPubkey(input)) {
    return { pubkey: input }
  }

  const { type, data } = decode(input)
  if (type === 'npub') {
    return { pubkey: data as string }
  } else if (type === 'nprofile') {
    return data as NostrUserRequest
  }

  throw new Error('Invalid profile identifier: ' + input)
}
